<script lang="ts">
  import { useForm, usePage } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import { Button } from '$lib/components/ui/button';
  import { Card, CardContent, CardHeader, CardTitle } from '$lib/components/ui/card';
  import { Input } from '$lib/components/ui/input';
  import { Label } from '$lib/components/ui/label';
  import { Link } from '@inertiajs/svelte';

  // 💡 优化：移除无意义的 name 字段，让表单回归核心
  const form = useForm({
    email_address: '', 
    password: '',
    password_confirmation: ''
  });

  const page = usePage();
  
  // Svelte 5 标准衍生计算消费语言包
  let flash = $derived((page.props as any).flash || {});
  let errors = $derived((page.props as any).errors || {});
  let translations = $derived((page.props as any).translations || {});
  let t = $derived(translations.auth || {});
  const legal = $derived(translations.legal || {});

  let timezone = '';
  let locale = '';

  onMount(() => {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    locale = navigator.language || 'en';
  });

  function submit(e: Event) {
    e.preventDefault(); // Svelte 5 规范：在函数中显式阻止默认事件
    form.post('/register', {
      data: {
        ...form, 
        timezone: timezone,
        locale: locale
      },
      onError: () => window.scrollTo({ top: 0, behavior: 'smooth' })
    });
  }
</script>

<div class="flex items-center justify-center p-4 min-h-[80vh] bg-zinc-50/50">
  <div class="w-full max-w-md space-y-6">
    <Card class="shadow-xl bg-white">
      <CardHeader>
        <CardTitle class="text-2xl text-center font-bold tracking-tight">{t.create_account}</CardTitle>
        <p class="text-center text-muted-foreground text-sm">{t.subtitle}</p>
      </CardHeader>

      <CardContent class="space-y-4">
        {#if flash.alert}
          <div class="bg-destructive/10 border border-destructive/20 text-destructive p-3 rounded-md text-sm">
            {flash.alert}
          </div>
        {/if}

        <form onsubmit={submit} class="space-y-4">
          <div class="space-y-2">
            <Label for="email_address">{t.email}</Label>
            <Input
              id="email_address"
              type="email" 
              placeholder="name@example.com"
              bind:value={form.email_address} required
              class={errors.email_address ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {#if errors.email_address}
              <p class="text-xs text-destructive">{Array.isArray(errors.email_address) ? errors.email_address[0] : errors.email_address}</p>
            {/if}
          </div>

          <div class="space-y-2">
            <Label for="password">{t.password}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t.password_hint}
              bind:value={form.password} required
              class={errors.password ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {#if errors.password}
              <p class="text-xs text-destructive">{Array.isArray(errors.password) ? errors.password[0] : errors.password}</p>
            {/if}
          </div>

          <div class="space-y-2">
            <Label for="password_confirmation">{t.confirm_password}</Label>
            <Input
              id="password_confirmation"
              type="password"
              placeholder={t.confirm_password_hint}
              bind:value={form.password_confirmation} required
              class={errors.password_confirmation ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {#if errors.password_confirmation}
              <p class="text-xs text-destructive">{Array.isArray(errors.password_confirmation) ? errors.password_confirmation[0] : errors.password_confirmation}</p>
            {/if}
          </div>

          <Button type="submit" class="w-full mt-2" disabled={form.processing}> 
            {form.processing ? t.registering : t.register_btn}
          </Button>
        </form>

        <div class="text-center text-xs text-muted-foreground mt-4 leading-relaxed">
          {legal.agreement_tip}
          <a href="/terms" target="_blank" class="underline underline-offset-4 hover:text-primary transition-colors">
            {legal.terms_link}
          </a>
          {legal.and}
          <a href="/privacy" target="_blank" class="underline underline-offset-4 hover:text-primary transition-colors">
            {legal.privacy_link}
          </a>
        </div>

        <div class="text-center text-sm pt-2">
          <span class="text-muted-foreground">{t.has_account}</span>
          <Link href="/login" class="text-primary hover:underline font-semibold ml-1">
            {t.go_to_login}
          </Link>
        </div>
      </CardContent>
    </Card>
  </div>
</div>